import { useCallback, useRef, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  Package,
  RotateCcw,
  Search,
  User,
} from 'lucide-react';
import apiService from '../../services/api';
import {
  buildUnifiedReturnSearchParams,
  CANAL_FILTER_OPTIONS,
  DATE_SCOPE_OPTIONS,
  defaultUnifiedSearchFormState,
  formatUnifiedClientLabel,
  resolveUnifiedClientSection,
  isCurrentSearchGeneration,
  isUnifiedSearchResponseStillValid,
  mapRefundRouteLabel,
  resolveUnifiedReturnBackNavigation,
  RETURN_STATUS_OPTIONS,
  unifiedReturnSearchHintMessage,
  unifiedSearchParamsFingerprint,
} from '../../utils/unifiedReturnExchangeSearch';
import { resolveU2S1aPosRetailHandoff } from '../../utils/unifiedReturnU2S1a';
import { resolveU2S1bWebEncaisseHandoff } from '../../utils/unifiedReturnU2S1b';
import { resolveU2Ux1StripeMesCommandesHandoff } from '../../utils/unifiedReturnU2Ux1';

function fmtMoney(value, devise = 'EUR') {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise }).format(n);
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function SaleResultCard({ hit, devise, onOpen }) {
  const clientLabel = formatUnifiedClientLabel(hit.client);
  const fs = hit.financeSummary || {};
  return (
    <button
      type="button"
      data-testid={`unified-return-hit-${hit.commandeId}`}
      onClick={() => onOpen(hit.commandeId)}
      className="w-full text-left bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:border-violet-400 hover:shadow-md transition-all active:scale-[0.99]"
    >
      <div className="flex flex-wrap justify-between gap-2 items-start">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 truncate">{hit.referencePrincipale}</p>
          {hit.referenceSecondaire ? (
            <p className="text-xs text-slate-500 truncate">{hit.referenceSecondaire}</p>
          ) : null}
          <p className="text-xs text-slate-500 mt-1">{fmtDateTime(hit.dateCommande)}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-slate-900">{fmtMoney(fs.montantTotalVente, devise)}</p>
          <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
            {hit.saleKindLabel || hit.canalVente}
          </span>
        </div>
      </div>
      {clientLabel ? (
        <p className="text-sm text-slate-600 mt-2 flex items-center gap-1.5">
          <User className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{clientLabel}</span>
        </p>
      ) : null}
      {hit.articlesResume?.length ? (
        <p className="text-sm text-slate-600 mt-1 flex items-start gap-1.5">
          <Package className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span className="line-clamp-2">{hit.articlesResume.join(' · ')}</span>
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 mt-3 text-xs">
        <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-800">
          {hit.paymentSummary?.moyenEncaissementLabel || '—'}
        </span>
        {fs.montantRembourseCumule > 0 ? (
          <span className="px-2 py-1 rounded-lg bg-orange-50 text-orange-800">
            Remb. {fmtMoney(fs.montantRembourseCumule, devise)}
          </span>
        ) : null}
        {hit.factureExiste ? (
          <span className="px-2 py-1 rounded-lg bg-blue-50 text-blue-800 flex items-center gap-1">
            <FileText className="h-3 w-3" /> Facture
          </span>
        ) : null}
      </div>
    </button>
  );
}

function DetailSection({ title, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-5">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3">{title}</h3>
      {children}
    </section>
  );
}

function PricingRecap({ pricingSummary, devise }) {
  const ps = pricingSummary;
  if (!ps) return null;
  const rows = [];
  if (ps.prixInitialArticlesTtc != null) {
    rows.push({ label: 'Prix initial articles', value: ps.prixInitialArticlesTtc, tone: 'muted' });
  }
  for (const part of ps.remisesAppliquees || []) {
    rows.push({
      label: part.label,
      value: -part.montantTtc,
      tone: 'discount',
      detail: part.pointsUtilises != null ? `${part.pointsUtilises} pts` : null,
    });
  }
  if (ps.sousTotalArticlesTtc != null && ps.prixInitialArticlesTtc != null) {
    rows.push({ label: 'Sous-total articles', value: ps.sousTotalArticlesTtc, tone: 'normal' });
  } else if (ps.sousTotalArticlesTtc != null) {
    rows.push({ label: 'Sous-total articles', value: ps.sousTotalArticlesTtc, tone: 'normal' });
  }
  if (ps.codePromo) {
    rows.push({ label: 'Code promo enregistré', value: ps.codePromo, tone: 'code' });
  }
  if (ps.fraisLivraisonTtc != null) {
    rows.push({ label: 'Frais de livraison', value: ps.fraisLivraisonTtc, tone: 'normal' });
  }
  if (ps.fraisExpeditionTtc != null) {
    rows.push({ label: 'Frais d’expédition', value: ps.fraisExpeditionTtc, tone: 'normal' });
  }
  if (!rows.length && ps.totalPayeTtc == null) return null;

  return (
    <dl className="space-y-2 text-sm">
      {rows.map((row, idx) => (
        <div key={`${row.label}-${idx}`} className="flex justify-between gap-3">
          <dt className="text-slate-600">
            {row.label}
            {row.detail ? <span className="block text-xs text-slate-400">{row.detail}</span> : null}
          </dt>
          <dd
            className={
              row.tone === 'code'
                ? 'font-mono text-xs text-slate-700 shrink-0'
                : row.tone === 'discount'
                  ? 'font-medium text-emerald-700 shrink-0'
                  : row.tone === 'muted'
                    ? 'text-slate-500 shrink-0'
                    : 'font-medium text-slate-900 shrink-0'
            }
          >
            {row.tone === 'code' ? row.value : fmtMoney(row.value, devise)}
          </dd>
        </div>
      ))}
      <div className="flex justify-between gap-3 pt-2 border-t border-slate-100">
        <dt className="font-bold text-slate-900">Total réglé</dt>
        <dd className="font-bold text-lg text-slate-900">{fmtMoney(ps.totalPayeTtc, devise)}</dd>
      </div>
    </dl>
  );
}

export default function UnifiedReturnExchangePanel({
  commercantId,
  devise = 'EUR',
  onBack,
  onOpenRetailReturn,
  /** U2-S1a — ouvre R6 avec ticket POS retail (moteur existant) */
  onNavigateToPosRetailReturn,
  /** U2-S1b — ouvre détail commande + modal SAV (ROUTE-2 existante) */
  onNavigateToWebEncaisseRefund,
  /** UX-1 — ouvre Mes Commandes (deep link, nouvel onglet) */
  onOpenMesCommandesForStripe,
  canOpenMesCommandes = false,
  showToast,
}) {
  const [step, setStep] = useState('search');
  const [form, setForm] = useState(defaultUnifiedSearchFormState);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [meta, setMeta] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const searchSeqRef = useRef(0);
  const lastFingerprintRef = useRef(null);

  const patchForm = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch, page: patch.page != null ? patch.page : 1 }));
    if (patch.q != null || patch.kind != null || patch.dateScope != null) {
      searchSeqRef.current += 1;
      setItems([]);
      setMeta(null);
      setPagination(null);
      setSearchError(null);
    }
  }, []);

  const runSearch = useCallback(
    async ({ pageOverride, kindOverride, preferBarcode = false } = {}) => {
      const nextForm = {
        ...form,
        page: pageOverride ?? form.page,
        kind: kindOverride ?? form.kind,
      };
      const built = buildUnifiedReturnSearchParams(nextForm, { preferBarcode });
      if (!built.ok) {
        setSearchError(built.clientMessage || 'Recherche invalide');
        return;
      }

      const seq = ++searchSeqRef.current;
      const fingerprint = unifiedSearchParamsFingerprint(built.params);
      lastFingerprintRef.current = fingerprint;
      setSearchLoading(true);
      setSearchError(null);

      try {
        const data = await apiService.searchUnifiedReturns(built.params, commercantId);
        if (!isCurrentSearchGeneration(seq, searchSeqRef.current)) return;
        if (
          !isUnifiedSearchResponseStillValid(fingerprint, built.params)
        ) {
          return;
        }

        if (data?.error || data?.code) {
          const msg =
            unifiedReturnSearchHintMessage(data.code || data.error, data.hints) ||
            data.message ||
            'Recherche impossible';
          setSearchError(msg);
          setItems([]);
          setMeta(data.meta || null);
          return;
        }

        const nextItems = data.items || [];
        const nextPage = Number(built.params.page) || 1;
        setItems((prev) => (nextPage > 1 ? [...prev, ...nextItems] : nextItems));
        setPagination(data.pagination || null);
        setMeta(data.meta || null);

        if (data.meta?.status === 'DISAMBIGUATION_REQUIRED') {
          setSearchError(null);
        } else if (!data.items?.length && data.meta?.status === 'NO_RESULTS') {
          setSearchError(null);
        }
      } catch (err) {
        if (!isCurrentSearchGeneration(seq, searchSeqRef.current)) return;
        const status = err?.status || err?.response?.status;
        if (status === 403 || status === 401) {
          setSearchError('Vous n’avez pas l’autorisation de consulter les retours unifiés.');
        } else {
          setSearchError(err?.message || 'Erreur réseau — réessayez.');
        }
        setItems([]);
      } finally {
        if (isCurrentSearchGeneration(seq, searchSeqRef.current)) {
          setSearchLoading(false);
        }
      }
    },
    [commercantId, form]
  );

  const openDetail = useCallback(
    async (commandeId) => {
      setStep('detail');
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(true);
      try {
        const data = await apiService.getUnifiedReturnPreview(commandeId, commercantId);
        setPreview(data);
      } catch (err) {
        const status = err?.status || err?.response?.status;
        if (status === 404) {
          setPreviewError('Vente introuvable ou hors boutique.');
        } else if (status === 403 || status === 401) {
          setPreviewError('Consultation non autorisée.');
        } else {
          setPreviewError(err?.message || 'Impossible de charger la fiche.');
        }
      } finally {
        setPreviewLoading(false);
      }
    },
    [commercantId]
  );

  const handleBack = () => {
    const action = resolveUnifiedReturnBackNavigation({ step, detailOpen: step === 'detail' });
    if (action === 'back_to_search') {
      setStep('search');
      setPreview(null);
      setPreviewError(null);
      return;
    }
    onBack?.();
  };

  const disambiguation = meta?.status === 'DISAMBIGUATION_REQUIRED' ? meta.disambiguation : null;

  return (
    <div className="pos-caisse-work-surface max-w-3xl mx-auto w-full" data-testid="unified-return-exchange-panel">
      <header className="pos-caisse-work-surface-header flex flex-wrap items-center gap-3 mb-4 sm:mb-6">
        <button
          type="button"
          data-testid="unified-return-back"
          onClick={handleBack}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 min-h-[44px] px-2 -ml-2 rounded-xl"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Retour</span>
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Retours et échanges</h2>
          <p className="text-sm text-slate-500">Consultation multicanale — sans remboursement</p>
        </div>
        {onOpenRetailReturn ? (
          <button
            type="button"
            data-testid="unified-return-open-retail-r6"
            onClick={onOpenRetailReturn}
            className="text-sm font-semibold text-fuchsia-700 hover:text-fuchsia-900 underline min-h-[44px] px-2"
          >
            Retour produits (retail)
          </button>
        ) : null}
      </header>

      {step === 'search' ? (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <label className="sr-only" htmlFor="unified-return-q">
              Recherche
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                <input
                  id="unified-return-q"
                  data-testid="unified-return-search-input"
                  type="search"
                  enterKeyHint="search"
                  autoComplete="off"
                  placeholder="Rechercher un client, article, ticket, facture, commande ou code-barres…"
                  className="w-full pl-10 pr-3 py-3 min-h-[48px] rounded-xl border border-slate-200 text-base focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  value={form.q}
                  onChange={(e) => patchForm({ q: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runSearch({ pageOverride: 1, preferBarcode: true });
                    }
                  }}
                />
              </div>
              <button
                type="button"
                data-testid="unified-return-search-submit"
                disabled={searchLoading}
                onClick={() => runSearch({ pageOverride: 1, preferBarcode: true })}
                className="shrink-0 min-h-[48px] px-5 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 disabled:opacity-60 flex items-center gap-2"
              >
                {searchLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                <span className="hidden sm:inline">Rechercher</span>
              </button>
            </div>

            <button
              type="button"
              data-testid="unified-return-filters-toggle"
              className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 min-h-[44px]"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Filtres facultatifs
            </button>

            {filtersOpen ? (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                <label className="block text-sm">
                  <span className="text-slate-600 font-medium">Période</span>
                  <select
                    data-testid="unified-return-date-scope"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 min-h-[44px]"
                    value={form.dateScope}
                    onChange={(e) => patchForm({ dateScope: e.target.value })}
                  >
                    {DATE_SCOPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                {form.dateScope === 'RANGE' ? (
                  <>
                    <label className="block text-sm">
                      <span className="text-slate-600 font-medium">Du</span>
                      <input
                        type="date"
                        data-testid="unified-return-date-min"
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 min-h-[44px]"
                        value={form.dateMin}
                        onChange={(e) => patchForm({ dateMin: e.target.value })}
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="text-slate-600 font-medium">Au</span>
                      <input
                        type="date"
                        data-testid="unified-return-date-max"
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 min-h-[44px]"
                        value={form.dateMax}
                        onChange={(e) => patchForm({ dateMax: e.target.value })}
                      />
                    </label>
                  </>
                ) : null}
                <label className="block text-sm">
                  <span className="text-slate-600 font-medium">Canal</span>
                  <select
                    data-testid="unified-return-canal"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 min-h-[44px]"
                    value={form.canalVente}
                    onChange={(e) => patchForm({ canalVente: e.target.value })}
                  >
                    {CANAL_FILTER_OPTIONS.map((o) => (
                      <option key={o.value || 'all'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-slate-600 font-medium">Statut retour</span>
                  <select
                    data-testid="unified-return-return-status"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 min-h-[44px]"
                    value={form.returnStatus}
                    onChange={(e) => patchForm({ returnStatus: e.target.value })}
                  >
                    {RETURN_STATUS_OPTIONS.map((o) => (
                      <option key={o.value || 'all'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
          </div>

          {disambiguation?.options?.length ? (
            <div
              className="rounded-2xl border border-amber-200 bg-amber-50 p-4"
              data-testid="unified-return-disambiguation"
            >
              <p className="font-semibold text-amber-950">Précisez le type de recherche</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {disambiguation.options.map((opt) => (
                  <button
                    key={opt.kind}
                    type="button"
                    data-testid={`unified-return-disambig-${opt.kind}`}
                    className="min-h-[44px] px-4 rounded-xl bg-white border border-amber-300 text-sm font-semibold hover:bg-amber-100"
                    onClick={() => {
                      patchForm({ kind: opt.kind });
                      runSearch({ pageOverride: 1, kindOverride: opt.kind });
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {searchError ? (
            <p
              className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
              data-testid="unified-return-search-error"
              role="alert"
            >
              {searchError}
            </p>
          ) : null}

          {searchLoading && !items.length ? (
            <div className="flex justify-center py-16" data-testid="unified-return-search-loading">
              <Loader2 className="h-10 w-10 animate-spin text-violet-600" />
            </div>
          ) : null}

          {!searchLoading && meta?.status === 'NO_RESULTS' ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-100" data-testid="unified-return-no-results">
              <RotateCcw className="h-12 w-12 mx-auto text-slate-200 mb-3" />
              <p className="text-slate-600 font-medium">Aucune vente trouvée</p>
              <p className="text-sm text-slate-500 mt-1">Élargissez la période ou modifiez les critères.</p>
            </div>
          ) : null}

          {items.length > 0 ? (
            <div className="space-y-3" data-testid="unified-return-results">
              {items.map((hit) => (
                <SaleResultCard key={hit.commandeId} hit={hit} devise={devise} onOpen={openDetail} />
              ))}
              {pagination?.hasMore ? (
                <button
                  type="button"
                  data-testid="unified-return-load-more"
                  disabled={searchLoading}
                  className="w-full min-h-[48px] rounded-xl border-2 border-violet-200 text-violet-800 font-semibold hover:bg-violet-50 disabled:opacity-60"
                  onClick={() => runSearch({ pageOverride: (pagination.page || 1) + 1 })}
                >
                  {searchLoading ? 'Chargement…' : 'Page suivante'}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {step === 'detail' ? (
        <div className="space-y-4" data-testid="unified-return-detail">
          {previewLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-10 w-10 animate-spin text-violet-600" />
            </div>
          ) : null}
          {previewError ? (
            <p className="text-red-700 bg-red-50 rounded-xl px-4 py-3" role="alert">
              {previewError}
            </p>
          ) : null}
          {preview && !previewLoading ? (
            <>
              <DetailSection title="Client">
                {(() => {
                  const clientSection = resolveUnifiedClientSection(preview.client);
                  return (
                    <p
                      className={`text-sm font-medium flex items-center gap-1.5 ${
                        clientSection.isPlaceholder ? 'text-slate-500 italic' : 'text-slate-900'
                      }`}
                      data-testid="unified-return-client-label"
                    >
                      <User className="h-4 w-4 shrink-0" />
                      {clientSection.displayLabel}
                    </p>
                  );
                })()}
              </DetailSection>

              <DetailSection title="Commande">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <dt className="text-slate-500">Référence</dt>
                    <dd className="font-semibold">{preview.commande?.numero || preview.commande?.id}</dd>
                  </div>
                  {preview.commande?.numeroTicket ? (
                    <div>
                      <dt className="text-slate-500">Ticket POS</dt>
                      <dd className="font-semibold">{preview.commande.numeroTicket}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-slate-500">Date</dt>
                    <dd>{fmtDateTime(preview.commande?.dateCommande)}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Canal</dt>
                    <dd>{preview.commande?.saleKindLabel || preview.commande?.canalVente}</dd>
                  </div>
                </dl>
              </DetailSection>

              <DetailSection title="Articles">
                <ul className="divide-y divide-slate-100">
                  {(preview.lignes || []).map((ln) => (
                    <li key={ln.ligneId} className="py-3 flex justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">{ln.nom}</p>
                        <p className="text-slate-500">Qté {ln.quantite} · {fmtMoney(ln.prixUnitaireTtc, devise)} u.</p>
                      </div>
                      <p className="font-semibold shrink-0">{fmtMoney(ln.prixLigneTtc, devise)}</p>
                    </li>
                  ))}
                </ul>
                {preview.pricingSummary ? (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                      Prix, remises et total
                    </p>
                    <PricingRecap pricingSummary={preview.pricingSummary} devise={devise} />
                  </div>
                ) : null}
              </DetailSection>

              <DetailSection title="Paiement et remboursement">
                <p className="text-sm mb-2">
                  <span className="text-slate-500">Moyen réel : </span>
                  <span className="font-semibold">{preview.paymentSummary?.moyenEncaissementLabel || '—'}</span>
                </p>
                <p className="text-sm mb-2">
                  <span className="text-slate-500">Remboursé : </span>
                  {fmtMoney(preview.financeSummary?.montantRembourseCumule, devise)}
                </p>
                <p className="text-sm">
                  <span className="text-slate-500">Solde remboursable : </span>
                  <span className="font-bold text-emerald-700">
                    {fmtMoney(preview.financeSummary?.soldeRemboursable, devise)}
                  </span>
                </p>
              </DetailSection>

              <DetailSection title="Documents">
                <ul className="text-sm space-y-1 text-slate-700">
                  <li>
                    Facture :{' '}
                    {preview.documents?.facture?.available
                      ? preview.documents.facture.numero || '—'
                      : '—'}
                  </li>
                  <li>
                    Ticket POS :{' '}
                    {preview.documents?.ticketPos?.available
                      ? preview.documents.ticketPos.numero || '—'
                      : '—'}
                  </li>
                  <li>Avoirs : {(preview.documents?.avoirs || []).length}</li>
                </ul>
              </DetailSection>

              <DetailSection title="Parcours de remboursement (indication serveur)">
                <p className="text-sm font-semibold text-slate-900">
                  {mapRefundRouteLabel(preview.paymentSummary?.refundRoute || preview.refundRoute)}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Circuit cible selon les paiements enregistrés. L’exécution passe par le parcours caisse autorisé (R6
                  ou autre), jamais par un second moteur.
                </p>
              </DetailSection>

              {(() => {
                const posHandoff = resolveU2S1aPosRetailHandoff(preview);
                const webHandoff = resolveU2S1bWebEncaisseHandoff(preview);
                const stripeMesCmd = resolveU2Ux1StripeMesCommandesHandoff(preview, {
                  canOpenMesCommandes,
                  commercantId,
                });
                const blocks = [];

                if (posHandoff.kind === 'action' && typeof onNavigateToPosRetailReturn === 'function') {
                  blocks.push(
                    <div
                      key="s1a"
                      className="rounded-2xl border border-violet-200 bg-violet-50/80 p-4"
                      data-testid="unified-return-u2-s1a-action"
                    >
                      <p className="text-sm font-semibold text-violet-900">Retour POS retail (R6)</p>
                      <p className="text-xs text-violet-800/90 mt-1 mb-3">
                        Ouvre le parcours « Retour produits » sur ce ticket. Confirmation et remboursement restent
                        gérés par R6 (même API qu’historiquement).
                      </p>
                      <button
                        type="button"
                        data-testid="unified-return-u2-pos-retail-cta"
                        className="w-full sm:w-auto min-h-[44px] px-5 py-2.5 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.99] transition"
                        onClick={() => onNavigateToPosRetailReturn(posHandoff.ticketId)}
                      >
                        Effectuer un retour
                      </button>
                    </div>
                  );
                }

                if (
                  stripeMesCmd.kind === 'action' &&
                  typeof onOpenMesCommandesForStripe === 'function'
                ) {
                  blocks.push(
                    <div
                      key="ux1-stripe"
                      className="rounded-2xl border border-sky-200 bg-sky-50/80 p-4"
                      data-testid="unified-return-ux1-stripe-mes-commandes"
                    >
                      <p className="text-sm font-semibold text-sky-900">Paiement Stripe en ligne</p>
                      <p className="text-xs text-sky-800/90 mt-1 mb-3">
                        Ouvre cette commande dans Mes Commandes pour le suivi et le remboursement Stripe
                        autorisé. Aucune opération financière depuis la caisse.
                      </p>
                      <button
                        type="button"
                        data-testid="unified-return-ux1-mes-commandes-cta"
                        className="w-full sm:w-auto min-h-[44px] px-5 py-2.5 rounded-xl bg-sky-600 text-white font-semibold hover:bg-sky-700 active:scale-[0.99] transition"
                        onClick={() => onOpenMesCommandesForStripe(stripeMesCmd.href)}
                      >
                        Ouvrir dans Mes Commandes
                      </button>
                    </div>
                  );
                }

                if (webHandoff.kind === 'action' && typeof onNavigateToWebEncaisseRefund === 'function') {
                  blocks.push(
                    <div
                      key="s1b"
                      className="rounded-2xl border border-emerald-200 bg-emerald-50/80 p-4"
                      data-testid="unified-return-u2-s1b-action"
                    >
                      <p className="text-sm font-semibold text-emerald-900">Remboursement C&amp;C (encaissement magasin)</p>
                      <p className="text-xs text-emerald-800/90 mt-1 mb-3">
                        Ouvre le parcours SAV caisse sur cette commande (ROUTE-2). Aucun remboursement automatique :
                        confirmation dans le modal existant.
                      </p>
                      <button
                        type="button"
                        data-testid="unified-return-u2-web-encaisse-cta"
                        className="w-full sm:w-auto min-h-[44px] px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 active:scale-[0.99] transition"
                        onClick={() => onNavigateToWebEncaisseRefund(webHandoff.commandeId)}
                      >
                        Ouvrir le remboursement caisse
                      </button>
                    </div>
                  );
                }

                if (blocks.length) {
                  return <div className="space-y-3">{blocks}</div>;
                }

                if (
                  stripeMesCmd.kind === 'disabled_permission' ||
                  stripeMesCmd.kind === 'disabled_flag'
                ) {
                  return (
                    <div
                      className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 opacity-90"
                      data-testid="unified-return-ux1-stripe-hint"
                    >
                      <p className="text-sm font-semibold text-slate-600">Paiement Stripe en ligne</p>
                      <p className="text-xs text-slate-500 mt-1">{stripeMesCmd.message}</p>
                    </div>
                  );
                }

                const hint =
                  posHandoff.kind === 'disabled_flag' || webHandoff.kind === 'disabled_flag'
                    ? posHandoff.message || webHandoff.message
                    : posHandoff.kind === 'disabled_server'
                      ? posHandoff.message
                      : webHandoff.kind === 'disabled_server'
                        ? webHandoff.message
                        : 'Utilisez « Retour produits (retail) » depuis l’accueil caisse pour les tickets POS, ou le circuit indiqué ci-dessus pour les autres ventes.';

                return (
                  <div
                    className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 opacity-90"
                    data-testid="unified-return-u2-placeholder"
                  >
                    <p className="text-sm font-semibold text-slate-600">Retour / remboursement</p>
                    <p className="text-xs text-slate-500 mt-1">{hint}</p>
                  </div>
                );
              })()}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
